import { quickID, type BiblePassageContent, type BibleViewOptions } from '@soulhx/fs-common';
import React, { useState, type JSX } from 'react';
import { type FootnoteData } from '../../dm/bible-types.js';
import { filterContent, generateItemMap } from './helpers/component-helpers.js';
import { RenderFootnote } from './helpers/RenderFootnote.js';
import { Ref } from './helpers/Ref.js';
import { Button } from 'react-bootstrap';
import { clsx } from '@soulhx/ui-common';
import { useTranslation } from 'react-i18next';

interface IRenderPassage {
  passage: string;
  children: BiblePassageContent[];
  copyright?: string;
  footnotes: FootnoteData[];
  crossReferences: FootnoteData[];
  options: BibleViewOptions;
  footnoteTitle: string;
  crossRefTitle: string;
  LoadMoreButton?: JSX.Element | null;
  fullCopyright?: boolean;
  abbreviationLocal: string;
  forceHideRef?: boolean;
}
export const RenderPassage = ({
  passage,
  children,
  copyright,
  footnotes,
  options,
  footnoteTitle,
  crossReferences,
  crossRefTitle,
  LoadMoreButton = null,
  fullCopyright = false,
  abbreviationLocal,
  forceHideRef = false,
}: IRenderPassage) => {
  const [showFullCopyrightText, setShowFullCopyrightText] = useState(false);
  const { t } = useTranslation(['bibleviewer']);

  const filteredChildren = filterContent(children, options);

  return (
    <div>
      <div className="scripture-styles reading-text">{filteredChildren.map(generateItemMap(options))}</div>

      {LoadMoreButton}

      <Ref
        abbreviationLocal={abbreviationLocal}
        copyright={copyright}
        fullCopyright={fullCopyright}
        passage={passage}
        forceHideRef={forceHideRef}
      />

      {footnotes.length > 0 && (
        <>
          <h5 className="mt-4">{footnoteTitle}</h5>

          <div className="scripture-styles reading-text">
            {footnotes.map((fn) => (
              <RenderFootnote fn={fn} key={quickID()} options={options} />
            ))}
          </div>
        </>
      )}

      {crossReferences.length > 0 && (
        <>
          <h5>{crossRefTitle}</h5>

          <div className="scripture-styles reading-text">
            {crossReferences.map((xr) => (
              <RenderFootnote fn={xr} key={quickID()} options={options} />
            ))}
          </div>
        </>
      )}

      {copyright && fullCopyright && (
        <div className="copyright-container">
          <div className="copyright-row">
            <Button
              variant="link"
              className={clsx({ 'p-0 align-baseline me-1': showFullCopyrightText })}
              size="sm"
              onClick={() => setShowFullCopyrightText((oldValue) => !oldValue)}
            >
              {t(showFullCopyrightText ? 'bibleviewer:showCopyrightBtnShowing' : 'bibleviewer:showCopyrightBtn', {
                abbrev: abbreviationLocal,
              })}
            </Button>
            <span className={clsx({ 'd-none': !showFullCopyrightText })}>{copyright}</span>
          </div>
        </div>
      )}
    </div>
  );
};
